import { useMemo, useState } from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
import {
  LBSheet,
  LBSheetContent,
  LBSheetHeader,
  LBSheetTitle,
  LBScrollArea,
  LBLoader,
  LBAlert,
  LBAlertDescription,
  LBChevronDownIcon,
  LBChevronRightIcon,
  LBButton,
  LBSelect,
  LBSelectTrigger,
  LBSelectValue,
  LBSelectContent,
  LBSelectItem,
  LBTooltip,
  LBTooltipTrigger,
  LBTooltipContent
} from '@linearb/buzz'
import { AiProviderLabel } from '../../custom-components/SensorsIcons'
import AccountLabel from '../../custom-components/AccountLabel'
import BackfillDeltaTiles from './BackfillDeltaTiles'
import { matchBackfillPreset } from './AiBackfillDialog'
import { useBackfillHistory } from '../../hooks/queries/useOrganizations'
import { useAiBackfill } from '../../hooks/useAiBackfill'
import { useConfirmBackfillResult } from '../../hooks/useConfirmBackfillResult'
import {
  inclusiveDays,
  parseUtc,
  RelativeDateTooltip
} from '../../utils/dateHelpers'
import { toneClassFor } from '../../utils/aiBackfillOutcome'
import { usd } from '../../lib/numberFormat'
import type { BackfillJob } from '../../hooks/useAiBackfill'
import type { AccountInfo, AiBackfillResult } from '../../types'

dayjs.extend(duration)

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  organizationId: string | number
  organizationName?: string
}

const ALL = '__all__'

interface FilterOption {
  value: string
  label: React.ReactNode
}

const FilterSelect: React.FC<{
  value: string
  onChange: (value: string) => void
  allLabel: string
  options: FilterOption[]
}> = ({ value, onChange, allLabel, options }) => (
  <LBSelect value={value} onValueChange={onChange}>
    <LBSelectTrigger className='h-8 w-auto min-w-32 text-xs'>
      <LBSelectValue placeholder={allLabel} />
    </LBSelectTrigger>
    <LBSelectContent>
      <LBSelectItem value={ALL}>{allLabel}</LBSelectItem>
      {options.map(o => (
        <LBSelectItem key={o.value} value={o.value}>
          {o.label}
        </LBSelectItem>
      ))}
    </LBSelectContent>
  </LBSelect>
)

// Older records predate the ran_by_account snapshot, so fall back to the email alone.
const ranByAccount = (r: AiBackfillResult): AccountInfo =>
  r.ran_by_account ?? { id: 0, email: r.ran_by }

const NO_FILTERS = { tool: ALL, ranBy: ALL, status: ALL }

export const PresetLabel: React.FC<{ label: string | null }> = ({ label }) =>
  label ? <span className='font-medium text-foreground'>{label} · </span> : null

const HistoryRow: React.FC<{ record: AiBackfillResult }> = ({ record }) => {
  const [open, setOpen] = useState(false)
  const { after, outcome } = useConfirmBackfillResult(record, open)
  const days = inclusiveDays(record.since, record.until)
  const recovered = outcome?.costRecovered ?? 0
  const preset = record.created_at
    ? matchBackfillPreset(
        record.since,
        record.until,
        parseUtc(record.created_at).subtract(
          record.duration_seconds ?? 0,
          'second'
        )
      )
    : null

  return (
    <div className='rounded-lg border border-border'>
      <button
        type='button'
        onClick={() => setOpen(o => !o)}
        className='flex w-full items-start gap-2 p-3 text-left hover:bg-muted/40'
      >
        {open ? (
          <LBChevronDownIcon className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
        ) : (
          <LBChevronRightIcon className='mt-0.5 h-4 w-4 shrink-0 text-muted-foreground' />
        )}
        <div className='flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-start sm:justify-between sm:gap-3'>
          <div className='flex min-w-0 flex-col gap-1'>
            <AiProviderLabel
              tool={record.tool}
              method={record.method}
              size={16}
            />
            <span className='text-xs text-muted-foreground'>
              <PresetLabel label={preset} />
              {record.since} → {record.until} · {days} days
            </span>
            {record.duration_seconds != null && (
              <span className='text-xs text-muted-foreground'>
                took{' '}
                {dayjs.duration(record.duration_seconds * 1000).format('m:ss')}
              </span>
            )}
          </div>
          <div className='flex min-w-0 flex-col gap-1 sm:items-end'>
            {outcome && (
              <span
                className={`text-sm font-semibold ${toneClassFor(outcome)}`}
              >
                {outcome.label}
                {recovered > 0 ? ` · +${usd.format(recovered)}` : ''}
              </span>
            )}
            {record.ran_by && (
              <LBTooltip>
                <LBTooltipTrigger asChild>
                  <span className='flex max-w-full min-w-0'>
                    <AccountLabel
                      account={ranByAccount(record)}
                      size={20}
                      textClassName='text-sm text-muted-foreground'
                      className='max-w-full sm:flex-row-reverse'
                    />
                  </span>
                </LBTooltipTrigger>
                <LBTooltipContent>{record.ran_by}</LBTooltipContent>
              </LBTooltip>
            )}
            <RelativeDateTooltip
              dateStr={record.created_at}
              className='text-xs text-muted-foreground'
              tooltipDatePattern='MMM D, YYYY ·'
            />
          </div>
        </div>
      </button>
      {open && (
        <div className='border-t border-border p-3'>
          <BackfillDeltaTiles
            before={record.before}
            after={after}
            outcome={outcome}
            tool={record.tool}
          />
        </div>
      )}
    </div>
  )
}

// In-progress backfills (from the provider) shown above the completed S3 records.
const RunningRow: React.FC<{ job: BackfillJob }> = ({ job }) => {
  const days = inclusiveDays(job.since, job.until)
  const preset = matchBackfillPreset(job.since, job.until, dayjs(job.startedAt))
  return (
    <div className='rounded-lg border border-border'>
      <div className='flex w-full items-start gap-2 p-3'>
        <LBLoader className='mt-0.5 h-4 w-4 shrink-0' />
        <div className='flex min-w-0 flex-col gap-1'>
          <AiProviderLabel tool={job.tool} method={job.method} size={16} />
          <span className='text-xs text-muted-foreground'>
            <PresetLabel label={preset} />
            {job.since} → {job.until} · {days} days · est. {job.estimateLabel}
          </span>
        </div>
        <span className='ml-auto text-sm font-semibold text-blue-600'>
          Running now
        </span>
      </div>
    </div>
  )
}

// Org-level backfill history from S3 (all SAs, newest first); rows expand to the delta grid.
const AiBackfillHistorySheet: React.FC<Props> = ({
  open,
  onOpenChange,
  organizationId,
  organizationName
}) => {
  const { data, isLoading, isError } = useBackfillHistory(organizationId, open)
  const { jobs } = useAiBackfill()
  const running = jobs.filter(
    j => j.state === 'running' && String(j.orgId) === String(organizationId)
  )
  const [filters, setFilters] = useState(NO_FILTERS)
  const setFilter =
    (key: keyof typeof NO_FILTERS) =>
    (value: string): void =>
      setFilters(f => ({ ...f, [key]: value }))

  const options = useMemo(() => {
    const records = data ?? []
    return {
      tools: _.uniq(records.map(r => r.tool).filter(Boolean)).map(t => ({
        value: t,
        label: <AiProviderLabel tool={t} size={14} />
      })),
      people: _.uniqBy(
        records.filter(r => r.ran_by),
        'ran_by'
      ).map(r => ({
        value: r.ran_by,
        label: (
          <AccountLabel
            account={ranByAccount(r)}
            size={16}
            textClassName='text-xs text-foreground'
          />
        )
      })),
      statuses: _.uniq(records.map(r => r.outcome?.label).filter(Boolean)).map(
        l => ({ value: l, label: l })
      )
    }
  }, [data])

  const filtered = (data ?? []).filter(
    r =>
      (filters.tool === ALL || r.tool === filters.tool) &&
      (filters.ranBy === ALL || r.ran_by === filters.ranBy) &&
      (filters.status === ALL || r.outcome?.label === filters.status)
  )
  const isFiltered = !_.isEqual(filters, NO_FILTERS)

  return (
    <LBSheet open={open} onOpenChange={onOpenChange}>
      <LBSheetContent
        side='right'
        className='w-full sm:max-w-xl p-0 flex flex-col'
      >
        <LBSheetHeader className='px-6 pt-6 pb-4'>
          <LBSheetTitle className='text-base'>Backfill history</LBSheetTitle>
          <span className='text-sm text-foreground'>
            {organizationName ?? `Org ${organizationId}`}{' '}
            <span className='text-xs text-muted-foreground'>
              (Org {organizationId})
            </span>
          </span>
        </LBSheetHeader>

        <LBScrollArea className='flex-1 px-6 pb-6'>
          <div className='flex flex-col gap-2'>
            {isLoading && (
              <div className='flex items-center gap-2 text-sm text-muted-foreground'>
                <LBLoader className='h-4 w-4' /> Loading history…
              </div>
            )}

            {isError && (
              <LBAlert variant='attention'>
                <LBAlertDescription>
                  Couldn&apos;t load backfill history.
                </LBAlertDescription>
              </LBAlert>
            )}

            {data && data.length > 1 && (
              <div className='flex flex-wrap items-center gap-2 pb-1'>
                <FilterSelect
                  value={filters.tool}
                  onChange={setFilter('tool')}
                  allLabel='All connectors'
                  options={options.tools}
                />
                <FilterSelect
                  value={filters.ranBy}
                  onChange={setFilter('ranBy')}
                  allLabel='Anyone'
                  options={options.people}
                />
                <FilterSelect
                  value={filters.status}
                  onChange={setFilter('status')}
                  allLabel='Any result'
                  options={options.statuses}
                />
                {isFiltered && (
                  <>
                    <span className='text-xs text-muted-foreground'>
                      {filtered.length} of {data.length}
                    </span>
                    <LBButton
                      variant='ghost'
                      size='sm'
                      className='h-8 text-xs'
                      onClick={() => setFilters(NO_FILTERS)}
                    >
                      Clear
                    </LBButton>
                  </>
                )}
              </div>
            )}

            {running.map(job => (
              <RunningRow key={job.id} job={job} />
            ))}

            {data && data.length === 0 && running.length === 0 && (
              <span className='text-sm text-muted-foreground'>
                No backfills recorded yet for this organization.
              </span>
            )}

            {isFiltered && filtered.length === 0 && (
              <span className='text-sm text-muted-foreground'>
                No backfills match these filters.
              </span>
            )}

            {filtered.map(record => (
              <HistoryRow
                key={
                  record.jobs_suite_uuid ??
                  record.created_at ??
                  `${record.since}-${record.until}-${record.tool}`
                }
                record={record}
              />
            ))}
          </div>
        </LBScrollArea>
      </LBSheetContent>
    </LBSheet>
  )
}

export default AiBackfillHistorySheet
